<script setup lang="ts">
import {
	computed,
	onBeforeUnmount,
	onMounted,
	ref
} from "vue"

type Props = {
	text: string
	seconds?: number
}

const props = withDefaults(defineProps<Props>(), {
	seconds: 5
})

const emit = defineEmits<{
	(e: 'finish'): void
	(e: 'cancel'): void
}>()

const countdown = ref(props.seconds)
let tickTimer: ReturnType<typeof setInterval> | null = null
let finishTimer: ReturnType<typeof setTimeout> | null = null
let finished = false

const rootStyle = computed(() => ({ '--ndt-duration': `${props.seconds}s` }))

const clearTimers = () => {
	if (tickTimer) {
		clearInterval(tickTimer)
		tickTimer = null
	}
	if (finishTimer) {
		clearTimeout(finishTimer)
		finishTimer = null
	}
}

const finish = () => {
	if (finished) {
		return
	}
	finished = true
	clearTimers()
	emit('finish')
}

onMounted(() => {
	tickTimer = setInterval(() => {
		countdown.value = Math.max(0, countdown.value - 1)
	}, 1000)
	finishTimer = setTimeout(finish, props.seconds * 1000)
})

onBeforeUnmount(clearTimers)

defineExpose({ flush: finish })
</script>

<template>
	<div class="notification-undo" :style="rootStyle">
		<div class="notification-undo__plate">
			<div class="ndt-ring">
				<svg class="ndt-ring__svg" viewBox="0 0 20 20">
					<circle class="ndt-ring__circle" cx="10" cy="10" r="9" />
				</svg>
				<span class="ndt-ring__num">{{ countdown }}</span>
			</div>
			<span class="notification-undo__text">{{ props.text }}</span>
			<button
				type="button"
				class="notification-undo__cancel"
				@click="emit('cancel')"
			>
				{{ $t('ui_cancel') }}
			</button>
		</div>
	</div>
</template>

<style scoped lang="scss">
.notification-undo {
	position: absolute;
	inset: 0;
	z-index: 3;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: rgba(255, 255, 255, 0.7);
	border-radius: 8px;

	&__plate {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		max-width: 100%;
		box-sizing: border-box;
		padding: 12px;
		border-radius: 8px;
		background-color: rgba(92, 92, 92, 0.95);
	}

	&__text {
		margin-left: 8px;
		font-size: 12px;
		line-height: 16px;
		font-weight: 500;
		color: #fff;
		white-space: nowrap;
	}

	&__cancel {
		margin-left: 16px;
		flex-shrink: 0;
		padding: 2px 8px;
		border: none;
		border-radius: 8px;
		background-color: #fff;
		font-family: inherit;
		font-size: 12px;
		line-height: 16px;
		color: #4D4D4D;
		cursor: pointer;
		transition: color 0.2s ease;

		&:hover {
			color: #3F884F;
		}
	}
}

.ndt-ring {
	position: relative;
	width: 20px;
	height: 20px;
	flex-shrink: 0;

	&__svg {
		width: 20px;
		height: 20px;
		transform: rotate(90deg) scaleX(-1);
	}

	&__circle {
		fill: none;
		stroke: #fff;
		stroke-width: 2;
		stroke-dasharray: 56.549;
		stroke-dashoffset: 0;
		animation: ndt-deplete var(--ndt-duration, 5s) linear forwards;
	}

	&__num {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 10px;
		line-height: 14px;
		font-weight: 600;
		color: #fff;
	}
}

@keyframes ndt-deplete {
	from { stroke-dashoffset: 0; }
	to { stroke-dashoffset: 56.549; }
}
</style>
